import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { distanceMetres, getPosition, GPS_RADIUS_METRES } from '../lib/gps';
import { addVisitPhotos } from '../lib/photos';
import { Lighthouse } from '../types';
import '../styles/CheckIn.css';

interface Props {
  appUserId: string | null;
  readOnly?: boolean;
}

type StampResult = {
  kind: 'stamped' | 'review' | 'already';
  name: string;
} | null;

function CheckInPage({ appUserId, readOnly = false }: Props) {
  const [lighthouses, setLighthouses] = useState<Lighthouse[]>([]);
  const [tier, setTier] = useState('Standard');
  const [search, setSearch] = useState('');
  const [lighthouseId, setLighthouseId] = useState<number | ''>('');
  const [gpsStatus, setGpsStatus] = useState<
    'idle' | 'checking' | 'pass' | 'fail' | 'error'
  >('idle');
  const [distance, setDistance] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [message, setMessage] = useState('');
  const [stampResult, setStampResult] = useState<StampResult>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase
      .from('lighthouses')
      .select('*')
      .order('name')
      .then(({ data }) => setLighthouses(data || []));
  }, []);

  const options = lighthouses.filter(
    (l) =>
      l.passport_type === tier &&
      l.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const selected = lighthouses.find((l) => l.id === lighthouseId);

  const runGpsCheck = async () => {
    if (!selected) return;
    setGpsStatus('checking');
    setMessage('');
    setStampResult(null);
    try {
      const pos = await getPosition();
      const d = distanceMetres(
        pos.latitude,
        pos.longitude,
        Number(selected.latitude),
        Number(selected.longitude)
      );
      setDistance(Math.round(d));
      setAccuracy(Math.round(pos.accuracy));
      setGpsStatus(d <= GPS_RADIUS_METRES ? 'pass' : 'fail');
    } catch (err: any) {
      setGpsStatus('error');
      setMessage(err.message || 'Could not read GPS. Check location permissions.');
    }
  };

  const submit = async (verified: boolean) => {
    if (!appUserId || !selected) return;
    setBusy(true);
    setMessage('');

    const { data: created, error } = await supabase
      .from('visits')
      .insert({
        user_id: appUserId,
        lighthouse_id: selected.id,
        visited_at: new Date().toISOString(),
        verified,
      })
      .select('id')
      .single();

    if (!error && created && photo) {
      await addVisitPhotos(appUserId, selected.id, created.id, [photo]);
    }

    if (error) {
      if (error.message.includes('duplicate') || error.code === '23505') {
        setStampResult({ kind: 'already', name: selected.name });
      } else {
        setMessage(error.message);
      }
    } else if (verified) {
      setStampResult({ kind: 'stamped', name: selected.name });
      setPhoto(null);
    } else {
      setStampResult({ kind: 'review', name: selected.name });
      setPhoto(null);
    }
    setBusy(false);
  };

  if (readOnly) {
    return (
      <div className="page checkin">
        <h1>Check In</h1>
        <div className="stamp-card review">
          <p className="stamp-title">Your membership has ended</p>
          <p className="stamp-body">
            Your stamps, photos and map are yours to keep and are all still here in
            your passport. Renew to check in at your next lighthouse.
          </p>
          <Link to="/join" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Renew membership
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page checkin">
      <h1>Check In</h1>

      <label className="field-label">Passport</label>
      <select
        value={tier}
        onChange={(e) => {
          setTier(e.target.value);
          setSearch('');
          setLighthouseId('');
          setGpsStatus('idle');
          setDistance(null);
          setStampResult(null);
          setMessage('');
        }}
      >
        <option>Standard</option>
        <option>Extreme</option>
        <option>Heritage</option>
      </select>

      <label className="field-label">Search lighthouses</label>
      <input
        type="text"
        className="search-input"
        placeholder="Start typing a name..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setLighthouseId('');
          setGpsStatus('idle');
          setDistance(null);
          setStampResult(null);
          setMessage('');
        }}
      />

      <label className="field-label">Lighthouse</label>
      <select
        value={lighthouseId}
        onChange={(e) => {
          setLighthouseId(Number(e.target.value) || '');
          setGpsStatus('idle');
          setDistance(null);
          setStampResult(null);
          setMessage('');
        }}
      >
        <option value="">
          {options.length === 0
            ? 'No lighthouses match your search'
            : 'Select a lighthouse'}
        </option>
        {options.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>

      {selected && gpsStatus !== 'pass' && gpsStatus !== 'fail' && (
        <button
          className="btn-primary"
          onClick={runGpsCheck}
          disabled={gpsStatus === 'checking'}
        >
          {gpsStatus === 'checking' ? 'Getting a GPS fix, hold still...' : 'Verify My Location'}
        </button>
      )}

      {gpsStatus === 'pass' && !stampResult && (
        <div className="gps-result gps-pass">
          Within range: {distance}m from {selected?.name}
          {accuracy !== null ? ` (GPS accuracy ${accuracy}m)` : ''}
        </div>
      )}
      {gpsStatus === 'fail' && !stampResult && (
        <>
          <div className="gps-result gps-fail">
            Too far: {distance}m away. You must be within {GPS_RADIUS_METRES}m to
            check in.
            {accuracy !== null ? ` Your phone's GPS accuracy is ${accuracy}m.` : ''}
          </div>
          {accuracy !== null && accuracy > 150 && (
            <p className="checkin-message">
              Your phone is only sharing an approximate location, so the distance
              above is not reliable. On iPhone, open Settings, then Privacy and
              Security, Location Services, then Safari Websites (or the browser you
              use) and turn on Precise Location. Then come back and try again.
            </p>
          )}
        </>
      )}
      {gpsStatus === 'error' && (
        <div className="gps-result gps-fail">{message}</div>
      )}

      {stampResult && (
        <div
          className={
            stampResult.kind === 'review'
              ? 'stamp-card review'
              : 'stamp-card success'
          }
        >
          <p className="stamp-title">
            {stampResult.kind === 'stamped' && 'Stamped!'}
            {stampResult.kind === 'already' && 'Already stamped'}
            {stampResult.kind === 'review' && 'Sent for review'}
          </p>
          <p className="stamp-name">{stampResult.name}</p>
          <p className="stamp-body">
            {stampResult.kind === 'stamped' &&
              'It is in your passport. Nice hunting.'}
            {stampResult.kind === 'already' &&
              'This one is already in your passport.'}
            {stampResult.kind === 'review' &&
              'We will take a look and stamp it if everything checks out.'}
          </p>
        </div>
      )}

      {gpsStatus === 'pass' && !stampResult && (
        <>
          <label className="field-label">Photo (optional)</label>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          />
          <button
            className="btn-primary"
            onClick={() => submit(true)}
            disabled={busy}
          >
            {busy ? 'Stamping...' : 'Collect Stamp'}
          </button>
        </>
      )}

      {gpsStatus === 'fail' && !stampResult && (
        <>
          <p className="checkin-message">
            At the lighthouse but GPS says otherwise? It happens at some
            locations. Send it for review and we will sort it out.
          </p>
          <label className="field-label">Photo (required, it is how we verify)</label>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          />
          {!photo && (
            <p className="checkin-message">
              Add a photo of you at the lighthouse to send this for review.
            </p>
          )}
          <button
            className="btn-primary"
            onClick={() => submit(false)}
            disabled={busy || !photo}
          >
            {busy ? 'Sending...' : 'Submit for review'}
          </button>
          <button className="link-btn" onClick={runGpsCheck} disabled={busy}>
            Try GPS again
          </button>
        </>
      )}

      {message && gpsStatus !== 'error' && (
        <p className="checkin-message">{message}</p>
      )}
    </div>
  );
}

export default CheckInPage;